// src/components/auth/GoogleLinkStep.tsx
//
// Shown when a user attempted Google sign-in but the email is
// already registered with email/password. We captured the Google
// OAuth credential in the store; this step asks the user for their
// existing password, signs in with email+password, then attaches
// the Google credential to that user.
//
// Net effect: the next time the user clicks "Continue with Google"
// for the same email, it Just Works — no more password prompt, no
// more "guest user" surprise. They've reconciled the two providers
// onto a single Firebase user with a single Firestore profile.
//
// Cancel → cancelGoogleLink() drops the captured credential and the
// modal returns to the regular tabbed login/signup view.

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Avatar,
  Stack,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google,
  ArrowForward,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { useT } from '../../i18n/useT';

interface Props {
  /** Called after the link succeeds and the user is signed in. The
   *  parent (AuthModal) typically closes itself in response. */
  onComplete: () => void;
}

export const GoogleLinkStep: React.FC<Props> = ({ onComplete }) => {
  const t = useT();
  const {
    pendingGoogleLink,
    completeGoogleLink,
    cancelGoogleLink,
    error,
    isLoading,
  } = useAuthStore();

  // TEMP DIAGNOSTIC: mount confirmation. If we see this log in the
  // console, the link step DEFINITELY rendered. If we don't see it,
  // AuthModal didn't reach this branch.
  React.useEffect(() => {
    console.log("[GoogleLinkStep] mounted with pending:", pendingGoogleLink);
    return () => {
      console.log("[GoogleLinkStep] unmounted");
    };
  }, []);

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Defensive: if the store cleared this out from underneath us
  // (e.g. another tab signed in), bail to the parent so it can
  // re-render the regular tabs.
  if (!pendingGoogleLink) {
    return null;
  }

  const email = pendingGoogleLink.email;
  // Initials from the email for the avatar — purely visual since
  // we don't have a display name to show here.
  const initials = (email || '?')
    .charAt(0)
    .toUpperCase();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim() || submitting) return;
    setSubmitting(true);
    try {
      await completeGoogleLink(password);
      // The store has already cleared pendingGoogleLink and the
      // auth listener has flipped isAuthenticated. Close the modal.
      onComplete();
    } catch {
      // Store has already set a friendly error message. Stay on
      // this step so the user can retry with the correct password.
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    cancelGoogleLink();
  };

  const busy = submitting || isLoading;

  return (
    <Box>
      <Stack spacing={2.5}>
        <Box sx={{ textAlign: 'center', pt: 1 }}>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              mb: 1,
            }}
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'primary.main',
                fontSize: 22,
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>
            <Box
              sx={{
                position: 'absolute',
                right: -4,
                bottom: -4,
                bgcolor: 'background.paper',
                borderRadius: '50%',
                p: 0.5,
                boxShadow: 1,
                display: 'flex',
              }}
            >
              <Google sx={{ fontSize: 18, color: '#4285F4' }} />
            </Box>
          </Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            
            {t("Link Google to your account")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{email}</strong>  {t("is already registered. Enter your password to sign in — we'll connect Google so you can use either next time.")}
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label={t("Password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            autoComplete="current-password"
            disabled={busy}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={busy || !password.trim()}
            endIcon={<ArrowForward />}
          >
            {busy ? 'Signing in…' : 'Sign in & link Google'}
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="text"
            size="small"
            onClick={handleCancel}
            disabled={busy}
          >
            
            {t("Cancel")}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
